// Modal dialog with a focus trap, Escape, backdrop click and an exit animation, rendered into
// document.body so it sits above the canvas and the HUD. `confirmDialog()` is the promise-based
// replacement for window.confirm; `<DialogHost/>` (mounted once in App) renders it.
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { create } from 'zustand';
import { Button, IconButton } from './Button';
import { Display, Eyebrow } from './Panel';
import { motionMs } from './Motion';
import { playSfx } from '../../core/audio';

/** Open dialogs, oldest first: only the top one answers Escape and keeps Tab inside it, so a
 *  dialog opened over another (the inventory over the arrival modal, a trade over the inventory)
 *  closes alone. */
const stack: symbol[] = [];
const isTop = (k: symbol) => stack[stack.length - 1] === k;

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  eyebrow?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  size?: 'narrow' | 'default' | 'wide' | 'full';
  /** False for dialogs the player must answer (no Escape, no backdrop close, no ×). */
  dismissible?: boolean;
  testId?: string;
  className?: string;
}

export function Modal({ open, onClose, title, eyebrow, children, footer, size = 'default', dismissible = true, testId, className = '' }: ModalProps) {
  const [mounted, setMounted] = useState(open);
  const [closing, setClosing] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const restoreRef = useRef<HTMLElement | null>(null);
  const stackKey = useRef(Symbol('modal'));
  const titleId = useId();

  useEffect(() => {
    if (open) { setMounted(true); setClosing(false); playSfx('ui.open'); return; }
    if (!mounted) return;
    playSfx('ui.close');
    setClosing(true);
    const t = setTimeout(() => { setMounted(false); setClosing(false); }, motionMs(220));
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!mounted || closing) return;
    restoreRef.current = document.activeElement as HTMLElement | null;
    const el = dialogRef.current;
    const first = el?.querySelector<HTMLElement>('[data-autofocus]') ?? el?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? el)?.focus();
    const key = stackKey.current;
    const onKey = (e: KeyboardEvent) => {
      if (!isTop(key)) return;
      if (e.key === 'Escape' && dismissible) { e.stopPropagation(); onClose(); return; }
      if (e.key !== 'Tab' || !el) return;
      const items = [...el.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((n) => n.offsetParent !== null);
      if (!items.length) { e.preventDefault(); return; }
      const i = items.indexOf(document.activeElement as HTMLElement);
      if (e.shiftKey && (i <= 0)) { e.preventDefault(); items[items.length - 1]!.focus(); }
      else if (!e.shiftKey && (i === items.length - 1 || i === -1)) { e.preventDefault(); items[0]!.focus(); }
    };
    document.addEventListener('keydown', onKey, true);
    return () => { document.removeEventListener('keydown', onKey, true); restoreRef.current?.focus?.(); };
  }, [mounted, closing, dismissible, onClose]);
  // on the stack from opening to closing; kept apart from the key handler, whose effect reruns
  // whenever its parent hands it a new onClose (that must not lift this dialog over a newer one)
  useEffect(() => {
    if (!mounted || closing) return;
    const key = stackKey.current;
    stack.push(key);
    return () => { const i = stack.indexOf(key); if (i >= 0) stack.splice(i, 1); };
  }, [mounted, closing]);

  if (!mounted) return null;
  return createPortal(
    <div className={`fk-modal ${closing ? 'fk-modal--closing' : ''}`} onMouseDown={(e) => { if (dismissible && e.target === e.currentTarget) onClose(); }} data-testid={testId ? `${testId}-backdrop` : undefined}>
      <div ref={dialogRef} className={`fk-panel fk-panel--ornate fk-modal__dialog ${size !== 'default' ? `fk-modal__dialog--${size}` : ''} ${className}`} role="dialog" aria-modal="true" aria-labelledby={title ? titleId : undefined} tabIndex={-1} data-testid={testId}>
        {(title || dismissible) && (
          <div className="fk-modal__head">
            <div className="fk-modal__title">{eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}{title && <Display size="md" as="h2" id={titleId}>{title}</Display>}</div>
            {dismissible && <IconButton label="Close" variant="ghost" className="fk-modal__close" onClick={onClose} data-testid={testId ? `${testId}-close` : 'modal-close'}>✕</IconButton>}
          </div>
        )}
        <div className="fk-modal__body">{children}</div>
        {footer && <div className="fk-modal__foot">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}

/* ---- confirm dialogs ---- */

export interface ConfirmOptions { title: ReactNode; text?: ReactNode; confirmLabel?: string; cancelLabel?: string; danger?: boolean; eyebrow?: ReactNode }
interface Pending extends ConfirmOptions { id: number; resolve: (ok: boolean) => void }
interface DialogStore { queue: Pending[]; ask(o: ConfirmOptions): Promise<boolean>; answer(id: number, ok: boolean): void }

let nextId = 1;
export const useDialogs = create<DialogStore>()((set, get) => ({
  queue: [],
  ask(o) { return new Promise<boolean>((resolve) => set({ queue: [...get().queue, { ...o, id: nextId++, resolve }] })); },
  answer(id, ok) { const p = get().queue.find((q) => q.id === id); if (!p) return; set({ queue: get().queue.filter((q) => q.id !== id) }); p.resolve(ok); },
}));

/** `if (await confirmDialog({ title: 'Sell the sword?', danger: true })) …` */
export const confirmDialog = (o: ConfirmOptions): Promise<boolean> => useDialogs.getState().ask(o);

export function DialogHost() {
  const head = useDialogs((s) => s.queue[0]);
  const answer = useDialogs((s) => s.answer);
  const [shown, setShown] = useState<Pending | undefined>(head);
  // keep the last dialog rendered during the exit animation
  useEffect(() => { if (head) setShown(head); }, [head]);
  const p = head ?? shown;
  if (!p) return null;
  const open = !!head;
  return (
    <Modal open={open} onClose={() => answer(p.id, false)} title={p.title} eyebrow={p.eyebrow ?? 'Are you sure?'} size="narrow" testId="confirm"
      footer={<><Button variant="ghost" onClick={() => answer(p.id, false)} data-testid="confirm-cancel">{p.cancelLabel ?? 'Cancel'}</Button><Button variant={p.danger ? 'danger' : 'primary'} onClick={() => answer(p.id, true)} data-testid="confirm-ok" data-autofocus>{p.confirmLabel ?? 'Confirm'}</Button></>}>
      {p.text && <p style={{ margin: 0 }}>{p.text}</p>}
    </Modal>
  );
}
